import React, { FC } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from 'app/modules/Betting/types';
import type { ReduxState } from 'app/redux/types';

import { ContextNamespace } from 'app/constants/Context';
import { withinBettingContext } from 'app/contexts/betting';
import { Icon } from 'app/design-system/components/Icon';
import { IconSize } from 'app/design-system/types/Icon';
import { useTranslation } from 'app/lang';
import { SlotType } from 'app/lib/betting/betting-entities';
import { SportId } from 'app/lib/betting/constants';
import { useMatchInFavorites } from 'app/lib/hooks/useMatchInFavorites';
import { ROUTE_PATH } from 'app/lib/routing/constants';
import { getDataGaEventByNamespace } from 'app/modules/Betting/utils/data-ga-event-by-namespace';
import { selectHasLiveStream } from 'app/modules/LiveStream/selectors';
import { composeMatchCenterPath, getPrefixBySlotType } from 'app/modules/Navigation/utils';
import { FavoritesIcon } from 'app/widgets/FavoritesIcon';
import { LiveStreamIcon } from 'app/widgets/LiveStreamIcon';

import dictionary from './MatchStreamingCtrl.dictionary.json';

import styles from './MatchStreamingCtrl.module.scss';

type MatchStreamingCtrlProps = {
  matchId: number;
  className?: string;
};

type StoreProps = {
  namespace: ContextNamespace;
  slotType: SlotType;
  sportId: SportId;
  tournamentId: number;
  tournamentUnionId: number;
  tournamentStageId: number;
  matchComment?: string;
  liveTracker?: boolean;
  hasLiveStream?: boolean;
};

const MatchStreamingCtrlWithoutHOC: FC<MatchStreamingCtrlProps & StoreProps> = ({
  namespace,
  slotType,
  matchId,
  sportId,
  tournamentId,
  tournamentUnionId,
  tournamentStageId,
  matchComment,
  liveTracker,
  hasLiveStream,
  className,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isInFavoritePage = pathname === ROUTE_PATH.FAVORITES;
  const isMatchInFavorites = useMatchInFavorites(matchId);
  const namespaceWithFavorites = isInFavoritePage ? ContextNamespace.FAVORITES : namespace;
  const favoritesDataGaEvent = isMatchInFavorites
    ? getDataGaEventByNamespace(namespaceWithFavorites, 'removeFavorites')
    : getDataGaEventByNamespace(namespaceWithFavorites, 'toFavorites');

  return (
    <div className={styles.root}>
      {matchComment && (
        <button type="button"
                className={styles.matchStreamingCtrlIcon}
                title={matchComment}>
          <Icon icon="match-comment"
                size={IconSize.MEDIUM}
                className={className || 'match-row-icon'} />
        </button>
      )}

      {liveTracker && (
        <Link
          title={t(dictionary.liveTrackerTitle)}
          className={styles.matchStreamingCtrlIcon}
          to={composeMatchCenterPath(
            { sportId, tournamentUnionId, tournamentId, tournamentStageId, matchId },
            getPrefixBySlotType(slotType),
          )}>
          <Icon icon="graphic-broadcast"
                size={14} />
        </Link>
      )}

      {hasLiveStream && (
        <LiveStreamIcon matchId={matchId}
                        dataGaEvent="live-broadcast" />
      )}

      <FavoritesIcon needAnimation
                     matchId={matchId}
                     dataGaEvent={favoritesDataGaEvent} />

    </div>
  );
};

const mapStateToProps = (
  state: ReduxState,
  {
    $betting: {
      namespace,
      content: {
        selectors: { matches },
      },
    },
    matchId,
  }: BettingContextProps,
) => {
  const match = matches(state).get(matchId);

  return {
    namespace,
    slotType: match?.slotType,
    sportId: match?.sportId,
    tournamentId: match?.tournamentId,
    tournamentUnionId: match?.tournamentUnionId,
    tournamentStageId: match?.tournamentStageId,
    matchComment: match?.matchComment,
    liveTracker: match?.attributes?.has('liveTracker'),
    hasLiveStream: !process.env.SERVER && selectHasLiveStream(state, matchId),
  };
};

export const MatchStreamingCtrl =  compose<FC<MatchStreamingCtrlProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(MatchStreamingCtrlWithoutHOC);
